import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import GetAppIcon from "@mui/icons-material/GetApp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import ShareIcon from "@mui/icons-material/Share";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import DatasetIcon from '@mui/icons-material/Dataset';
import OutputIcon from '@mui/icons-material/Output';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    Box,
    Collapse,
    Fab,
    CircularProgress,
    TableCell,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { dayjs } from "../../Services/dateUtil";
import StateIndicator from "../General/StateIndicator";
import EditLabelDialog from "./components/EditLabelDialog";
import ShareDialog from "./components/ShareDialog";
import VersionSelector from "./components/VersionSelector";
import api from '../../Services/api';
import { HomeAppContext } from "../Home";

const useStyles = makeStyles((theme) => ({
  detailsContainer: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 40,
    paddingRight: 20
  }
}));

const formatText = (string) =>
  string
    ? string.split("\\n").map((item, key) => {
        return (
          <span key={key}>
            {item}
            <br />
          </span>
        );
      })
    : "";
const parProperties = (string) => {
  try {
    const json = JSON.parse(string);
    return formatText(JSON.stringify(json, null, "\t"));
  } catch (e) {
    return string;
  }
};

const RenderExecutionHistories = ({ entry }) => {
  return (
    <Box p={2}>
      {entry.aiEngines?.length ? (
        entry.aiEngines.map((aiEngine) => (
          <Typography key={aiEngine.id} variant="body2">
            Execution history for {aiEngine.text} is not available.
          </Typography>
        ))
      ) : (
        <Typography variant="body2">No execution history available.</Typography>
      )}
    </Box>
  );
};

const Entry = ({
  entry,
  allowActions = true,
  isAdmin = false,
  onEdit,
  onRemove,
  onClone,
  onExecute,
  onExport
}) => {
  const classes = useStyles();
  const [show, setShow] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharingOption, setSharingOption] = useState("private");
  const showActions =
    allowActions && (isAdmin ? true : entry.role.name !== "Admin");
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [currentPromptContent, setCurrentPromptContent] = useState(entry.prompt);
  const [loading, setLoading] = useState(false);
  const { homeState, homeDispatch } = useContext(HomeAppContext);

  const fetchVersionContent = useCallback(
    async (versionNumber) => {
      try {
        setLoading(true);
        const response = await api.get(
          `/api/ai-prompts/${entry.id}/version-content/${versionNumber}`
        );

        if (response.data && response.data.prompt) {
          setCurrentPromptContent(response.data.prompt);
        }
      } catch (error) {
        setCurrentPromptContent(entry.prompt);
      } finally {
        setLoading(false);
      }
    },
    [entry.id, entry.prompt]
  );

  useEffect(() => {
    const fetchVersionData = async () => {
      if (!entry.id) return;
      
      try {
        setLoading(true);
        const versionsResponse = await api.get(`/api/ai-prompts/${entry.id}/versions`);
        
        if (versionsResponse.data && versionsResponse.data.length > 0) {
          const versionsData = versionsResponse.data;
          setVersions(versionsData);
          
          const activeVersion = versionsData.find(v => v.isActive);
          const versionToSelect = activeVersion ? 
            activeVersion.versionNumber : 
            versionsData[0].versionNumber;
          
          setSelectedVersion(versionToSelect);
          
          await fetchVersionContent(versionToSelect);
        }
      } catch (error) {
        console.error("Error fetching version data:", error);
        homeDispatch({
          type: "NOTIFICATION",
          data: { message: "Failed to load prompt versions", type: "error" }
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchVersionData();
  }, [entry.id, entry.prompt, fetchVersionContent, homeDispatch]);

  const handleVersionChange = (versionNumber) => {
    setSelectedVersion(versionNumber);
    fetchVersionContent(versionNumber);
  };

  const handleMakeVersionActive = async () => {
    if (!selectedVersion) return;
    
    try {
      setLoading(true);
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Setting version as active", loading: true }
      });
      
      const response = await api.post(`/api/ai-prompts/${entry.id}/set-active-version`, {
        versionNumber: selectedVersion
      });
      
      if (response.data) {
        homeDispatch({
          type: "NOTIFICATION",
          data: { message: "Version set as active successfully", type: "success" }
        });
        
        // Refresh versions to update UI
        const versionsResponse = await api.get(`/api/ai-prompts/${entry.id}/versions`);
        if (versionsResponse.data) {
          setVersions(versionsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error setting active version:", error);
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Failed to set active version", type: "error" }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteVersion = async () => {
    if (!selectedVersion) return;
    
    try {
      setLoading(true);
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Executing prompt version", loading: true }
      });
      
      const response = await api.post(`/api/ai-prompts/${entry.id}/execute-version`, {
        versionNumber: selectedVersion
      });
      if (response.status === 200) {
        homeDispatch({
          type: "NOTIFICATION",
          data: { message: "Prompt version executed successfully", type: "success" }
        });
        
        const versionsResponse = await api.get(`/api/ai-prompts/${entry.id}/versions`);
        if (versionsResponse.data) {
          setVersions(versionsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error executing version:", error);
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Failed to execute prompt version", type: "error" }
      });
    } finally {
      setLoading(false);
    }
  };

  const getSharingOption = (shareType) => {
    switch (shareType) {
      case 0:
        return "private";
      case 1:
        return "public-read-only";
      case 2:
        return "public";
      default:
        return "private";
    }
  };

  const handleShareClick = () => {
    console.log(entry.createdBy.id, homeState.userProfile.userId);
    setSharingOption(getSharingOption(entry.shareType));
    setShareDialogOpen(true);
  };

  const handleShareSave = async (newOption) => {
    setSharingOption(newOption);

    try {
      const sharingOptionDto = {
        sharingId: entry.id,
        sharingOption: newOption
      };

      const response = await api.put(
        `/api/ai-prompts/sharing`,
        sharingOptionDto
      );

      if (response.status === 200) {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: "Sharing option updated successfully",
            type: "success"
          }
        });
      } else {
        throw new Error("Failed to update sharing option");
      }
      console.log("newOption", newOption);
      entry.shareType = ["private", "public-read-only", "public"].indexOf(
        newOption
      );
    } catch (error) {
      console.error("Error updating sharing option:", error);
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Error updating sharing option", type: "error" }
      });
    }
  };

  const handleClickEdit = (event) => {
    onEdit(event, entry);
    setShowCommands(false);
  };

  const handleClickRemove = (event) => {
    onRemove(event, entry);
    setShowCommands(false);
  };

  const handleClickClone = (event) => {
    onClone(event, entry);
    setShowCommands(false);
  };

  const handleClickExecute = (event) => {
    onExecute(event, entry);
    setShowCommands(false);
  };

  const handleClickExport = (event) => {
    onExport(event, entry);
    setShowCommands(false);
  };

  const toggle = () => {
    setShow((show) => !show);
  };

  const [showCommands, setShowCommands] = useState(false);
  const commandBoxRef = useRef(null);

  const toggleCommands = () => setShowCommands(!showCommands);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        commandBoxRef.current &&
        !commandBoxRef.current.contains(event.target)
      ) {
        setShowCommands(false);
      }
    };

    if (showCommands) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCommands]);

  return (
    <React.Fragment>
      <TableRow sx={{ "&>td": { borderBottomStyle: "none" } }}>
        <TableCell>
          <Grid container alignItems="center">
            <Grid item>
              <IconButton onClick={toggle} size="small">
                {show ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            </Grid>
            <Grid item style={{ flex: 1 }}>
              <Typography variant="body2" display="inline">
                {entry.id}
              </Typography>
            </Grid>
          </Grid>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {entry.promptName}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {entry.aiPromptClients.map((e) => e.text).join(", ")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {entry.aiEngines.map((engine) => engine.text).join(", ")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {entry.createdBy ? entry.createdBy.fullName : ""}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {entry.modifiedUser ? entry.modifiedUser.fullName : ""}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {dayjs(entry.modifiedAt)
              .utc("z")
              .local()
              .format("YYYY-MM-DD, HH:mm:ss")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" display="inline">
            {entry.tags?.join(", ")}
          </Typography>
        </TableCell>
        <TableCell style={{ textAlign: "center" }}>
          {showActions && (
            <Box position="relative" display="inline-block" ref={commandBoxRef}>
              <IconButton onClick={toggleCommands} color="primary">
                <SettingsIcon />
              </IconButton>

              {showCommands && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: "100%",
                    transformOrigin: "top right",
                    transform: "translateY(-50%)",
                    mr: 1,
                    p: 1,
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    bgcolor: "background.paper",
                    boxShadow: 3,
                    borderRadius: "12px",
                    zIndex: 10,
                    animation: showCommands
                      ? "openBoxEffect 0.5s ease forwards"
                      : "none",
                    "@keyframes openBoxEffect": {
                      "0%": {
                        opacity: 0,
                        transform: "scale(0.3) translateY(-50%)",
                        boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)"
                      },
                      "60%": {
                        opacity: 1,
                        transform: "scale(1.1) translateY(-50%)",
                        boxShadow: "0px 10px 10px rgba(0, 0, 0, 0.2)"
                      },
                      "100%": {
                        opacity: 1,
                        transform: "scale(1) translateY(-50%)",
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
                      }
                    }
                  }}
                >
                  <Tooltip title="Execute" placement="top">
                    <Fab
                      size="small"
                      color="primary"
                      onClick={handleClickExecute}
                    >
                      <PlayArrowIcon />
                    </Fab>
                  </Tooltip>
                  {(entry.createdBy.id === homeState.userProfile.userId ||
                    entry.shareType === 2 || isAdmin) && (
                    <Tooltip title="Edit" placement="top">
                      <Fab size="small" color="primary" onClick={handleClickEdit}>
                        <EditIcon />
                      </Fab>
                    </Tooltip>
                  )}
                  {((entry.createdBy.id !== homeState.userProfile.userId &&
                    entry.shareType === 1) || isAdmin) && (
                      <Tooltip title="View Details" placement="top">
                        <Fab
                          size="small"
                          color="primary"
                          onClick={handleClickEdit}
                        >
                          <VisibilityIcon />
                        </Fab>
                      </Tooltip>
                    )}
                  <Tooltip title="Clone" placement="top">
                    <Fab size="small" color="primary" onClick={handleClickClone}>
                      <FileCopyIcon />
                    </Fab>
                  </Tooltip>
                  {(entry.createdBy.id === homeState.userProfile.userId || isAdmin) && (
                    <Tooltip title="Delete" placement="top">
                      <Fab
                        size="small"
                        color="primary"
                        onClick={handleClickRemove}
                      >
                        <DeleteIcon />
                      </Fab>
                    </Tooltip>
                  )}
                  <Tooltip title="Export to CSV" placement="top">
                    <Fab size="small" color="primary" onClick={handleClickExport}>
                      <GetAppIcon />
                    </Fab>
                  </Tooltip>
                  {(entry.createdBy.id === homeState.userProfile.userId || isAdmin) && (
                    <Tooltip title="Share" placement="top">
                      <Fab
                        size="small"
                        color="primary"
                        onClick={handleShareClick}
                      >
                        <ShareIcon />
                      </Fab>
                    </Tooltip>
                  )}
                  <Tooltip title="Download prompt" placement="top">
                    <Fab size="small" color="primary" onClick={handleClickExport}>
                      <GetAppIcon />
                    </Fab>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow sx={{ "&>td": { borderBottomStyle: "none" } }}>
        <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={show} mountOnEnter unmountOnExit>
            <Box className={classes.detailsContainer}>
                <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "10px", width: "40%"  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DeviceThermostatIcon/>
                        <Typography variant="overline">Temperature:</Typography>
                      </div>
                      <Typography variant="overline" fontWeight="bold">{parProperties(entry.temperature)}</Typography>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DatasetIcon/>
                        <Typography variant="overline">Dataset:</Typography>
                      </div>
                      <Typography variant="overline" fontWeight="bold">{entry.dataset.id !== 0 ? parProperties(entry.dataset.text) : "NONE"}</Typography>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <OutputIcon/>
                        <Typography variant="overline">Output:</Typography>
                      </div>
                      <Typography variant="overline" fontWeight="bold">{entry.isJson ? "JSON" : "TEXT"}</Typography>
                    </div>
                    <div style={{ display: "flex", flexDirection:"column", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DescriptionIcon/>
                        <Typography variant="overline">Description:</Typography>
                      </div>
                      <Typography variant="caption">{parProperties(entry.description)}</Typography>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "60%" }}>
                    <div style={{ display: "flex", whiteSpace: "nowrap", gap: "10px", alignItems: "center" }}>
                      <VersionSelector
                                versions={versions}
                                selectedVersion={selectedVersion}
                                loading={loading}
                                onVersionChange={handleVersionChange}
                                onMakeVersionActive={handleMakeVersionActive}
                                onExecuteVersion={handleExecuteVersion}
                      />
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", height: "200px", overflowY: "auto", padding	: "10px", border: "1px solid #ccc", borderRadius: "4px" }}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Typography variant="caption">{parProperties(currentPromptContent)}</Typography>
                    )}
                    </div>
                  </div>
                </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {show && (
        <TableRow>
          <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
            <Collapse in={show} mountOnEnter unmountOnExit>
              <Box className={classes.detailsContainer}>
                <Grid container spacing={2}>
                  <Grid item sm={12}>
                    <RenderExecutionHistories key={entry.id} entry={entry} />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onSave={handleShareSave}
        currentSharingOption={sharingOption}
      />
    </React.Fragment>
  );
};

export default Entry;
